import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import type { MenuCategory } from "../../types/menu";

interface MenuNavProps {
  categories: MenuCategory[];
}

const SCROLL_OFFSET = 128;

export default function MenuNav({ categories }: MenuNavProps) {
  const [activeId, setActiveId] = useState<string>(categories[0]?.id ?? "");
  const navRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const isProgrammaticScroll = useRef(false);
  const clearScrollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (isProgrammaticScroll.current) return;

      let current = categories[0]?.id ?? "";
      for (const { id } of categories) {
        const section = document.getElementById(id);
        if (!section) continue;
        const top = section.getBoundingClientRect().top;
        if (top <= SCROLL_OFFSET + 40) {
          current = id;
        }
      }
      setActiveId(current);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [categories]);

  useEffect(() => {
    const btn = buttonRefs.current[activeId];
    const nav = navRef.current;
    if (!btn || !nav) return;
    const btnLeft = btn.offsetLeft;
    const btnWidth = btn.offsetWidth;
    const navWidth = nav.offsetWidth;
    const target = btnLeft - navWidth / 2 + btnWidth / 2;
    nav.scrollTo({ left: target, behavior: "smooth" });
  }, [activeId]);

  const scrollTo = (id: string) => {
    const section = document.getElementById(id);
    if (!section) return;

    if (clearScrollTimer.current) clearTimeout(clearScrollTimer.current);

    isProgrammaticScroll.current = true;
    setActiveId(id);

    const top =
      section.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET;
    window.scrollTo({ top, behavior: "smooth" });

    clearScrollTimer.current = setTimeout(() => {
      isProgrammaticScroll.current = false;
    }, 900);
  };

  return (
    <motion.nav
      id="menu-nav"
      aria-label="Categorías del menú"
      className="sticky mx-2 sm:mx-4 rounded-2xl top-18 z-40 bg-white/85 backdrop-blur-xl border border-sand-dark/50 shadow-lg shadow-black/5"
      initial={{ opacity: 0, y: -12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.35, ease: "easeOut" }}
    >
      <div
        ref={navRef}
        className="flex gap-1.5 overflow-x-auto px-4 sm:px-6 py-2.5 no-scrollbar max-w-7xl mx-auto"
      >
        {categories.map(({ id, name }) => (
          <button
            key={id}
            ref={(el) => {
              buttonRefs.current[id] = el;
            }}
            onClick={() => scrollTo(id)}
            className={`relative whitespace-nowrap px-4 py-2 rounded-full text-sm font-bold transition-colors duration-200 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${
              activeId === id
                ? "text-white"
                : "text-warm-gray hover:text-primary active:scale-95"
            }`}
          >
            {activeId === id && (
              <motion.span
                layoutId="nav-pill"
                className="absolute inset-0 bg-primary rounded-full shadow-md shadow-primary/25"
                transition={{ type: "spring", stiffness: 380, damping: 30 }}
              />
            )}
            <span className="relative z-10">{name}</span>
          </button>
        ))}
      </div>
    </motion.nav>
  );
}
